import type { MailboxInformation } from "@1js/sydney-types";
import { MailboxType } from "@1js/sydney-types";
import type { MailboxInfo } from "owa-client-types";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import { isMailboxSharedOrDelegate } from "owa-account-shared-mailbox-utils";
import { isCloudCacheBizchatEnabled, getUnifiedInboxResolvedMailboxInfoOrSelf, } from "owa-session-store";
import { getCoprincipalAccountForMailboxInfo } from "owa-account-source-list-store";
import { isCloudCache } from "owa-account-source-list-internal";

export function requiresMailboxRestriction(mailboxInfo: MailboxInfo | undefined): boolean {
    return getMailboxInformationForAnnotation(mailboxInfo) !== undefined;
}

function getEffectiveMailboxInfo(mailboxInfo: MailboxInfo): MailboxInfo {
    return getUnifiedInboxResolvedMailboxInfoOrSelf(mailboxInfo);
}

function isSharedMailboxContext(mailboxInfo: MailboxInfo): boolean {
    return (isMailboxSharedOrDelegate(mailboxInfo) ||
        mailboxInfo.isExplicitLogon === true ||
        mailboxInfo.type === "SharedMailbox");
}

export function getMailboxInformationForAnnotation(mailboxInfo: MailboxInfo | undefined): MailboxInformation | undefined {
    if (!mailboxInfo) {
        return undefined;
    }
    
    // If in UIB, check if annotations are required for the mailboxInfo that will be used for bizchat
    const effectiveMailboxInfo = getEffectiveMailboxInfo(mailboxInfo);
    
    if (isGlobalFeatureEnabled("acct-enable-shared-in-bizchat") &&
        isSharedMailboxContext(effectiveMailboxInfo)) {
        return {
            mailboxType: MailboxType.SharedMailbox,
            mailboxSmtpAddress: effectiveMailboxInfo.mailboxSmtpAddress,
            auxiliaryMailboxGuid: effectiveMailboxInfo.auxiliaryMailboxGuid ?? undefined,
        };
    }
    
    if (isCloudCacheBizchatEnabled(effectiveMailboxInfo)) {
        const account = getCoprincipalAccountForMailboxInfo(effectiveMailboxInfo);
        
        if (account && isCloudCache(account)) {
            return {
                mailboxType: MailboxType.LinkedMailbox,
                mailboxSmtpAddress: account.mailboxInfo.userIdentity,
                auxiliaryMailboxGuid: account.mailboxInfo.auxiliaryMailboxGuid ?? undefined,
            };
        }
    }
    
    return undefined;
}

/**
 * Returns the mailbox restriction used by summarize Message CIQ annotations.
 * Sydney currently accepts SharedMailbox but not GroupMailbox, so group requests use the
 * SharedMailbox value as a compatibility mapping while retaining the group's SMTP routing data.
 */
export function getMailboxInformationForSummarizeAnnotation(mailboxInfo: MailboxInfo | undefined): MailboxInformation | undefined {
    if (!mailboxInfo) {
        return undefined;
    }
    
    const effectiveMailboxInfo = getEffectiveMailboxInfo(mailboxInfo);
    if (effectiveMailboxInfo.type !== "GroupMailbox" &&
        !isSharedMailboxContext(effectiveMailboxInfo)) {
        return getMailboxInformationForAnnotation(effectiveMailboxInfo);
    }
    
    return {
        mailboxType: MailboxType.SharedMailbox,
        mailboxSmtpAddress: effectiveMailboxInfo.mailboxSmtpAddress,
        auxiliaryMailboxGuid: effectiveMailboxInfo.auxiliaryMailboxGuid ?? undefined,
    };
}

export function isConversationCIQSupportedForSummarize(mailboxInfo: MailboxInfo): boolean {
    const effectiveMailboxInfo = getEffectiveMailboxInfo(mailboxInfo);
    return (effectiveMailboxInfo.type !== "GroupMailbox" &&
        !isSharedMailboxContext(effectiveMailboxInfo));
}
